import { useEffect } from 'react';

import {
  getShareLinkForcedThemeFromColorMode,
  type ShareLinkColorMode,
} from '~/components/v2/components/shared/share-link-design/shareLinkColorMode';
import { useShareLinkForcedThemeStore } from '~/components/v2/store/features/share-link-theme/useShareLinkForcedThemeStore';

/*
 * The share-link-theme store's mount adapter: forces the document theme to the link's `colorMode`
 * while a public share-link surface is mounted, by feeding `useShareLinkForcedThemeStore` (read by
 * `AppThemeProvider` at the app root — see its comment for why forcing happens at the document
 * level, not on the page subtree). `'dark'`/`'light'` force; `'toggle'` or an unknown wire value
 * leave the visitor's preference in charge. Callers that own the document must pass absent
 * `colorMode` through `getShareLinkPublicColorMode` first so legacy links force Light instead of
 * leaking workspace/OS theme. Passing `undefined` here means "do not force" (the editor Edit
 * canvas). The cleanup clears the store on unmount so navigating away — including closing the
 * authenticated standalone preview route, which renders the page in-app — snaps the document back
 * to the visitor's own theme.
 *
 * This client-side feed is what themes surfaces the ISR pageProps can't: passcode-verified links
 * (their presentationSettings only exist client-side) and the editor preview's live draft.
 */
export const usePublicShareLinkForcedTheme = (colorMode: ShareLinkColorMode | undefined): void => {
  const setForcedTheme = useShareLinkForcedThemeStore((state) => state.setForcedTheme);

  useEffect(() => {
    setForcedTheme(getShareLinkForcedThemeFromColorMode(colorMode));

    return () => setForcedTheme(undefined);
  }, [colorMode, setForcedTheme]);
};
